import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { toast } from 'react-toastify';
import { PaymentMethod, PaymentMethodType } from '@monofront/types';
import SidePanel from '@monofront/design-system/organisms/SidePanel';
import Button from '@monofront/design-system/components/Button';
import Arrow from '@monofront/design-system/visuals/Icons/Arrow';
import { PaymentMethodProvider } from '@features/PaymentMethods/PaymentMethodContext';
import NewPaymentMethodList from '../NewPaymentMethodList';
import SavedPaymentMethodList from '../SavedPaymentMethodList';

interface IUpdatePaymentMethodPanelProps {
  isOpen: boolean;
  isDunning: boolean;
  closeSidePanel: () => void;
  refetchPaymentQuery: () => void;
  primaryPaymentMethod?: PaymentMethod;
}

const UpdatePaymentMethodPanel: React.FC<IUpdatePaymentMethodPanelProps> = ({
  isOpen = false,
  isDunning = false,
  closeSidePanel,
  refetchPaymentQuery,
  primaryPaymentMethod,
}) => {
  const tAccount = useTranslations('Account');
  const tGlobal = useTranslations('Global');
  const tManageSubs = useTranslations('Account.manageMySubscriptionSection');
  const [paymentMethod, setPaymentMethod] = useState<null | PaymentMethodType>(
    null,
  );

  const ConfirmMessage = (
    <p>
      {tAccount.rich(
        isDunning ? 'dunningPaymentMethodUpdated' : 'paymentMethodUpdated',
        {
          strong: (children) => <strong>{children}</strong>,
        },
      )}
    </p>
  );

  const resetAndClosePanel = () => {
    setPaymentMethod(null);
    closeSidePanel();
  };

  let panelTitle;

  switch (paymentMethod) {
    case PaymentMethodType.CB:
      panelTitle = tManageSubs('updatePaymentCBTitle');
      break;
    case PaymentMethodType.IBAN:
      panelTitle = tManageSubs('updatePaymentSEPATitle');
      break;
    case PaymentMethodType.PAYPAL:
      panelTitle = tManageSubs('updatePaymentPaypalTitle');
      break;
    default:
      panelTitle = tManageSubs('updatePaymentTitle');
  }

  return (
    <PaymentMethodProvider>
      <SidePanel.SidePanel isOpen={isOpen} onClose={resetAndClosePanel}>
        <SidePanel.SidePanelHeader onClose={() => resetAndClosePanel()}>
          {paymentMethod && (
            <Button
              variant="clear"
              onClick={() => setPaymentMethod(null)}
              ariaLabel={tGlobal('back')}
            >
              <Arrow
                headDirection="left"
                color="var(--black)"
                width={14}
                height={14}
                aria-hidden
              />
            </Button>
          )}
          <h2 className="title-xs title">{panelTitle}</h2>
        </SidePanel.SidePanelHeader>
        <SidePanel.SidePanelBody UNSAFE_className="!tw-px-4 !tw-pt-6 !tw-pb-24">
          {!paymentMethod && (
            <SavedPaymentMethodList
              primaryPaymentMethod={primaryPaymentMethod}
              onUpdatePaymentMethodFinish={() => {
                refetchPaymentQuery();

                toast(ConfirmMessage, {
                  progressClassName: 'toast-green-bar',
                });

                resetAndClosePanel();
              }}
            />
          )}

          <section className="[form+&]:tw-mt-10">
            {!paymentMethod && (
              <h3 className="tw-lf-body-bold-md">
                {tManageSubs('useNewPaymentMethod')}
              </h3>
            )}

            <NewPaymentMethodList
              setPaymentMethod={setPaymentMethod}
              paymentMethod={paymentMethod}
              onUpdatePaymentMethodFinish={() => {
                refetchPaymentQuery();

                toast(ConfirmMessage, {
                  progressClassName: 'toast-green-bar',
                });

                resetAndClosePanel();
              }}
            />
          </section>
        </SidePanel.SidePanelBody>
      </SidePanel.SidePanel>
    </PaymentMethodProvider>
  );
};

export default UpdatePaymentMethodPanel;
